<template>
  <div :class="unsetClass ? '' : 'form-group row'">
    <form-label
      v-if="showLabel"
      :for-i-d="formLabelId"
      :is-required="true">
      {{ capitalize($t('names.name', { n: 1 })) }}
    </form-label>
    <div :class="unsetClass ? '' : `col-sm-${props.columnSize}`">
      <input
        :id="formLabelId"
        v-model="name"
        name="name"
        type="text"
        :disabled="disabled"
        :readonly="readonly"
        :class="inputClass"
        autocomplete="off" />
      <span
        v-show="errors?.has('name')"
        :data-entity-type="formLabelErrorId"
        class="help is-danger">
        {{ errors?.first('name') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed } from 'vue';

  import { FormLabel } from '@/common/components/controls';
  import { BaseEntityProps } from '@/common/components/entity-form/BaseEntityProps';
  import EntityFormHelper from '@/common/components/entity-form/EntityFormHelper';
  import EntityProperty from '@/common/models/EntityProperty';
  import rootStore from '@/store';
  import MessageCollection from '@/validator/MessageCollection';

  interface IProps extends BaseEntityProps {
    errors: MessageCollection;
    columnSize?: number;
    showLabel?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    unsetClass?: boolean;
    property: EntityProperty<unknown>;
  }

  const props = withDefaults(defineProps<IProps>(), {
    columnSize: 8,
    showLabel: true,
    disabled: false,
    readonly: false,
  });

  const entityFormHelper = computed<EntityFormHelper>(() => new EntityFormHelper(props.entity));

  const formLabelId = computed(() => entityFormHelper.value.getFormLabelId('edit', 'name'));
  const formLabelErrorId = computed(() => entityFormHelper.value.getFormLabelId('edit', 'name_error'));
  const inputClass = computed(() => {
    return { 'form-control': true, input: true, 'is-danger': props.errors?.has('name') };
  });
  const name = computed({
    get() {
      return props.entity?.name;
    },
    set(value: string) {
      if (props.entity == null || props.property == null) {
        console.warn('Entity or property is not defined');
        return;
      }

      let normalizedValue = value;
      if (props.property.preMutationTransform) {
        normalizedValue = props.property.preMutationTransform(value, props.entity);
      }

      const payload = {
        value: normalizedValue,
      };
      payload[
        `${props.property.editableClassName.slice(0, 1).toLowerCase()}${props.property.editableClassName.slice(1)}`
      ] = props.entity;
      if (!props.property.mutation) {
        throw new Error('Mutation is not defined');
      }

      if (typeof props.property.mutation === 'string') {
        rootStore.commit(props.property.mutation, payload);
      } else {
        props.property.mutation(payload);
      }
    },
  });
</script>

<style scoped lang="scss"></style>
